import React from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import { preventNonNumericalInput } from '@shared/utils/preventNonNumericalInput';
import { StyledAmountButtonsContainer, getAmountButtonOverrides } from './AnyAmountInput.styles';
import { FormControl } from './FormControl';
import { Input } from './Input';
import { useCashFundsTelemetry } from '../../../../CashFunds.telemetry';
import { useTranslation } from '@shared/core';
import { useCurrencyFormatter } from '@shared/utils/currency';

export type AnyAmountInputProps = Readonly<{
  amount: string;
  onAmountChange: (amount: string) => void;
  currencySymbol: string;
  currencyCode: string;
  fundId: string;
  fieldAmountLabel: string;
  isDisabled?: boolean;
  shouldShowAmountButtons?: boolean;
  error?: string;
  touched?: boolean;
}>;

export const AnyAmountInput: React.FC<AnyAmountInputProps> = ({
  amount,
  onAmountChange,
  currencySymbol,
  currencyCode,
  fundId,
  fieldAmountLabel,
  isDisabled = false,
  shouldShowAmountButtons = true,
  error,
  touched,
}) => {
  const { tq } = useTranslation('cashFundsGuest');
  const telemetry = useCashFundsTelemetry();
  const { formatCurrency } = useCurrencyFormatter();
  const hasError = !!error && !!touched;
  const currentAmountNumber = Number(amount);
  const errorId = `amount-error-${fundId}`;

  const predefinedAmounts = [100, 200, 300, 400, 500, 1000];

  const handleAmountButtonClick = (buttonAmount: number) => {
    telemetry.predefinedAmountSelected(fundId, buttonAmount, currencyCode);
    onAmountChange(buttonAmount.toString());
  };

  return (
    <>
      {shouldShowAmountButtons && (
        <StyledAmountButtonsContainer
          gap={3}
          marginBottom={5}
          role="group"
          aria-label={tq`Quick amount selection`}
        >
          {predefinedAmounts.map(buttonAmount => (
            <ButtonV2
              key={buttonAmount}
              variant="outline"
              intent="neutral"
              size="md"
              onClick={() => handleAmountButtonClick(buttonAmount)}
              overrides={getAmountButtonOverrides(!!(amount && currentAmountNumber === buttonAmount))}
              aria-pressed={!!(amount && currentAmountNumber === buttonAmount)}
            >
              {formatCurrency({
                priceFloatingPointDecimalString: buttonAmount.toString(),
                priceCurrencyCode: currencyCode,
                formatForm: 'short',
                hideZeroDecimals: true,
              })}
            </ButtonV2>
          ))}
        </StyledAmountButtonsContainer>
      )}
      <FormControl
        label={fieldAmountLabel}
        isInvalid={hasError}
        error={hasError ? error : undefined}
        overrides={{
          Hint: {
            props: hasError ? { id: errorId, role: 'alert' } : {},
          },
        }}
      >
        <Input
          startElement={currencySymbol}
          isInvalid={hasError}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
          aria-label={fieldAmountLabel}
          value={amount}
          onChange={e => onAmountChange(e.target.value)}
          type="number"
          onKeyDown={preventNonNumericalInput}
          tabIndex={0}
          disabled={isDisabled}
        />
      </FormControl>
    </>
  );
};
